// functions
import { connect, useSelector } from 'react-redux';
import * as ComponentFunctions from './ProtocolContract.Functions';
import { buildValidity, contractStatusMeta, describeValidity } from './ProtocolContract.Summary';
import { useNavigate } from 'react-router-dom';
import ContractsSelectorComponent from '../../../../commons/component/Inputs/ContractSelector/ContractSelectorComponent';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import { Contract } from '../../../../models/Entities/Contracts/Contract';
import { AppState } from '../../../../redux/reducers';
import { CheckProtocolContractAssociation } from '../../../../services/Contract.Services';
import { Spinner } from '@fluentui/react-components';
import {
    CheckmarkCircle16Regular,
    DocumentText24Regular,
    LinkDismiss24Regular,
    LockClosed16Regular,
    Open16Regular,
    PlugConnected16Regular,
    Star16Regular,
    Warning16Regular
} from '@fluentui/react-icons';

////i18Next
import { useTranslation } from 'react-i18next';

// Styles
import './ProtocolContract.Styles.css';
import { ConfirmationButtonComponent } from '../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';

const formatDate = (date: Date | string | undefined) => (date ? moment(date).format('DD/MM/YYYY') : undefined);

/** Resultado de consultar si el contrato elegido ya está atado a otro protocolo. */
type AssociationCheck =
    | { state: 'checking' }
    | { state: 'free' }
    | { state: 'linked'; protocolId: number }
    | { state: 'error' };

interface ContractCardProps {
    contract: Contract;
    isPrimary?: boolean;
    isPreview?: boolean;
    footer?: React.ReactNode;
}

/**
 * Ficha del contrato, a la medida de lo que un administrativo necesita de un
 * vistazo: qué contrato es, en qué estado está y hasta cuándo rige. El resto
 * (monedas, adendas, etapas, conceptos) vive en la página del contrato.
 */
const ContractCard = ({ contract, isPrimary, isPreview, footer }: ContractCardProps) => {
    const status = contractStatusMeta(contract.contractStatus);
    const validity = buildValidity(contract.contractStartDate, contract.contractEndDate);
    const marks = [
        isPrimary ? { key: 'primary', icon: <Star16Regular />, label: 'Principal' } : undefined,
        contract.contractVerified ? { key: 'verified', icon: <CheckmarkCircle16Regular />, label: 'Verificado' } : undefined,
        contract.isLocked ? { key: 'locked', icon: <LockClosed16Regular />, label: 'Bloqueado' } : undefined
    ].filter(Boolean) as { key: string; icon: JSX.Element; label: string }[];

    return (
        <article className={`pc-card${isPreview ? ' pc-card--preview' : ''}`}>
            <div className="pc-card__main">
                <span className="pc-card__icon" aria-hidden><DocumentText24Regular /></span>
                <div className="pc-card__body">
                    <div className="pc-card__top">
                        <span className={`pc-tag pc-tag--${status.tone}`}>{status.label}</span>
                        {marks.map((mark) => (
                            <span key={mark.key} className="pc-tag pc-tag--mark">{mark.icon}{mark.label}</span>
                        ))}
                    </div>
                    <span className="pc-card__number" title={contract.contractNumber}>
                        {contract.contractNumber || `Contrato #${contract.id}`}
                    </span>
                    {contract.contractDescription && (
                        <span className="pc-card__description" title={contract.contractDescription}>
                            {contract.contractDescription}
                        </span>
                    )}
                    <div className={`pc-validity pc-validity--${validity.state}`}>
                        <span className="pc-validity__dates">
                            {formatDate(contract.contractStartDate) || 'Sin inicio'}
                            <span className="pc-validity__sep" aria-hidden>→</span>
                            {formatDate(contract.contractEndDate) || 'Sin fin'}
                        </span>
                        <span className="pc-validity__note">{describeValidity(validity)}</span>
                        {validity.percent !== undefined && (
                            <span
                                className="pc-validity__bar"
                                role="progressbar"
                                aria-label="Vigencia transcurrida"
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={validity.percent}
                            >
                                <span style={{ width: `${validity.percent}%` }} />
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {footer && <footer className="pc-card__footer">{footer}</footer>}
        </article>
    );
};

export const ProtocolContractComponent = (props: ComponentFunctions.Props) => {

    const navigate = useNavigate();
    const [t] = useTranslation();
    const accessToken = useSelector((state: AppState) => (state as any)?.oidc?.user?.access_token as string | undefined);

    const [selectedContractToAssociate, setSelectedContractToAssociate] = useState<Contract | undefined>(undefined);
    const [check, setCheck] = useState<AssociationCheck | undefined>(undefined);

    // effects
    useEffect(() => {
        props.Protocol && props.GetAllProtocolContractAssociations(props.ProtocolId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.ProtocolId, props.Protocol !== undefined]);

    useEffect(() => {
        props.Protocol?.contractAssociation?.contractId && props.GetContractById(props.Protocol?.contractAssociation.contractId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.Protocol?.contractAssociation?.contractId]);

    // Cada contrato elegido se verifica contra la API: un contrato que ya
    // liquida otro protocolo no se puede asociar acá.
    useEffect(() => {
        const contractId = selectedContractToAssociate?.id;
        if (contractId === undefined || !accessToken) {
            setCheck(undefined);
            return;
        }

        let cancelled = false;
        setCheck({ state: 'checking' });
        CheckProtocolContractAssociation(contractId, accessToken)
            .then((protocolId) => {
                if (cancelled) return;
                setCheck(
                    protocolId === null || protocolId === undefined || protocolId === props.ProtocolId
                        ? { state: 'free' }
                        : { state: 'linked', protocolId }
                );
            })
            .catch(() => !cancelled && setCheck({ state: 'error' }));

        return () => {
            cancelled = true;
        };
    }, [selectedContractToAssociate?.id, accessToken, props.ProtocolId]);

    const protocolName = (protocolId: number) => {
        const protocol = props.Protocols?.find((item: Protocol) => item.id === protocolId);
        return protocol?.title || protocol?.fullTitle || `#${protocolId}`;
    };

    if (!props.Protocol) {
        return (
            <div className="pc-container">
                <div className="pc-loading">
                    <Spinner size="tiny" /> {t('protocolContract.onVerification.loading')}
                </div>
            </div>
        );
    }

    const association = props.Protocol.contractAssociation;

    // ── Con contrato ──
    if (association?.contractId) {
        return (
            <div className="pc-container">
                {props.Contract ? (
                    <ContractCard
                        contract={props.Contract}
                        isPrimary={association.isPrimary}
                        footer={
                            <>
                                <button
                                    type="button"
                                    className="pc-btn pc-btn--primary"
                                    onClick={() => navigate(`/contracts/${association.contractId}`)}
                                >
                                    <Open16Regular /> Ver contrato
                                </button>
                                <ConfirmationButtonComponent
                                    ButtonId={'disassociateContract'}
                                    ButtonIconName={'RemoveLink'}
                                    ButtonText={t('protocolContract.buttons.disassociate')}
                                    ButtonCssClass={'pc-disassociate'}
                                    ConfirmationButtonText={t('protocolContract.buttons.disassociate')}
                                    ConfirmationButtonCssClass={'danger'}
                                    CancelButtonText={t('protocolContract.buttons.cancelButton')}
                                    CancelButtonCssClass={''}
                                    Title={t('protocolContract.buttons.disassociateContract')}
                                    Description={`Va a desasociar el contrato ${props.Contract.contractNumber} del protocolo ${props.Protocol.fullTitle}`}
                                    ElementTextHeader={props.Contract.contractNumber || ''}
                                    ElementTextInformation={t('protocolContract.buttons.information')}
                                    isOperationExecuting={false}
                                    isOperationExecutedWithError={false}
                                    OperationError={undefined}
                                    onConfirmation={() => props.DisassociateContractFromProtocol(association)}
                                    onCancel={() => {}}
                                />
                            </>
                        }
                    />
                ) : (
                    <div className="pc-loading">
                        <Spinner size="tiny" /> Cargando contrato…
                    </div>
                )}
            </div>
        );
    }

    // ── Sin contrato ──
    const canAssociate = check?.state === 'free';

    return (
        <div className="pc-container">
            <div className="pc-empty">
                <span className="pc-empty__icon" aria-hidden><LinkDismiss24Regular /></span>
                <div className="pc-empty__text">
                    <span className="pc-empty__title">{t('protocolContract.noContract.unassociatedProtocol')}</span>
                    <span className="pc-empty__hint">
                        Sin contrato no hay precios para valorizar los consumos ni liquidaciones. Buscá el contrato y asocialo.
                    </span>
                </div>
            </div>

            <div className="pc-picker">
                <span className="pc-picker__label">Buscar contrato</span>
                <ContractsSelectorComponent
                    onSelectedContract={(selectedContracts: Contract[]) => setSelectedContractToAssociate(selectedContracts[0])}
                    ContractsSelected={selectedContractToAssociate ? [selectedContractToAssociate.id] : []}
                    MultiselectEnable={false}
                />
            </div>

            {selectedContractToAssociate && (
                <ContractCard
                    contract={selectedContractToAssociate}
                    isPreview
                    footer={
                        <>
                            {check?.state === 'checking' && (
                                <span className="pc-check">
                                    <Spinner size="extra-tiny" /> {t('protocolContract.onVerification.verifying')}
                                </span>
                            )}
                            {check?.state === 'free' && (
                                <span className="pc-check pc-check--ok">
                                    <CheckmarkCircle16Regular /> {t('protocolContract.onVerification.verificationOk')}
                                </span>
                            )}
                            {check?.state === 'linked' && (
                                <span className="pc-check pc-check--warn">
                                    <Warning16Regular /> Ya está asociado al protocolo {protocolName(check.protocolId)}.
                                </span>
                            )}
                            {check?.state === 'error' && (
                                <span className="pc-check pc-check--warn">
                                    <Warning16Regular /> No se pudo verificar el contrato. Probá elegirlo de nuevo.
                                </span>
                            )}
                            <button
                                type="button"
                                className="pc-btn pc-btn--primary"
                                disabled={!canAssociate}
                                onClick={() => props.AssociateContractToProtocol(selectedContractToAssociate.id, props.Protocol.id)}
                            >
                                <PlugConnected16Regular /> {t('protocolContract.buttons.associateContract')}
                            </button>
                        </>
                    }
                />
            )}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ProtocolContractComponent as any);
